import React from "react";
import FormModal from "./FormModal";
import { ROLE_LABELS } from "../../utils/optimizationRaci";

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

/** Rate difference against the incumbent, in plain language. */
const RateDelta = ({ delta, money, compact = false }) => {
  if (delta == null || delta === 0) return null;
  const cheaper = delta < 0;
  const amount = money ? money(Math.abs(delta)) : Math.abs(delta);
  return (
    <span className={`po-jobpick-delta ${cheaper ? "is-cheaper" : "is-pricier"}`}>
      {cheaper ? "▼" : "▲"} {amount}/hr{compact ? "" : cheaper ? " less" : " more"}
    </span>
  );
};

/**
 * Pick the job that holds one RACI role on one task.
 *
 * Advisory-first, like every other control on the optimization page: choosing a
 * job changes nothing until Apply, the resulting numbers are re-scored by the
 * backend what-if engine (never computed here), and a skill gap annotates the
 * choice rather than preventing it. The rate delta shown is a preview of the
 * direction of travel, not the saved figure.
 */
const RaciJobEditModal = ({
  open,
  taskName,
  role,
  currentJob,
  candidates = [],
  busy = false,
  error = "",
  money,
  onConfirm,
  onCancel,
}) => {
  const [selectedId, setSelectedId] = React.useState(null);
  const [query, setQuery] = React.useState("");

  const currentId = currentJob?.job_id ?? null;
  const currentName =
    currentJob?.name || currentJob?.job_name || "Unassigned";
  const currentRate = currentJob?.hourly_rate ?? currentJob?.hourlyRate ?? null;
  const roleLabel = ROLE_LABELS[role] || role;

  // Re-arm on every open: a stale selection from the previous row must never
  // carry over into a different task/role. Nothing starts selected — the only
  // useful choice here is a DIFFERENT job, so Apply stays disabled until one is
  // picked rather than arming on a no-op.
  React.useEffect(() => {
    if (!open) return;
    setSelectedId(null);
    setQuery("");
  }, [open, currentId, taskName, role]);

  // The incumbent is already named in the change preview, so listing it again is
  // a row that can only be a no-op.
  const alternatives = React.useMemo(
    () => candidates.filter((c) => String(c.job_id) !== String(currentId)),
    [candidates, currentId],
  );

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return alternatives;
    return alternatives.filter((c) => c.job_name.toLowerCase().includes(q));
  }, [alternatives, query]);

  // Recommended = the optimization named it for this role. Kept as a visible
  // group rather than a sort order, so "why is this one first" is answerable.
  const groups = React.useMemo(() => {
    const recommended = filtered.filter((c) => c.eligibility === "eligible");
    const others = filtered.filter((c) => c.eligibility !== "eligible");
    return [
      {
        key: "recommended",
        label: "Recommended — skill-qualified for this role",
        rows: recommended,
      },
      {
        key: "others",
        // Only a real "rest of the list" when there is something above it.
        label: recommended.length ? "All other jobs" : "All jobs",
        rows: others,
      },
    ].filter((g) => g.rows.length > 0);
  }, [filtered]);

  const chosen = alternatives.find((c) => String(c.job_id) === String(selectedId));
  const isChange = chosen != null;
  const fmtRate = (v) => (v == null ? "—" : money ? `${money(v)}/hr` : `${v}/hr`);
  const delta =
    chosen && currentRate != null && chosen.hourly_rate != null
      ? chosen.hourly_rate - currentRate
      : null;

  return (
    <FormModal
      open={open}
      size="large"
      // Makes the candidate list the ONLY scroller: without it the panel scrolls
      // too and the dialog shows two scrollbars side by side.
      className="po-jobpick-panel"
      title={`Reassign ${roleLabel}`}
      onCancel={busy ? () => {} : onCancel}
      footer={
        <>
          <button
            type="button"
            className="primary-btn"
            disabled={!isChange || busy}
            onClick={() => chosen && onConfirm(chosen)}
            title={
              isChange
                ? "Apply this reassignment and re-score the process"
                : "Pick a job below to enable this"
            }
          >
            {busy ? "Re-scoring…" : "Apply reassignment"}
          </button>
          <button
            type="button"
            className="secondary-btn"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
        </>
      }
    >
      <div className="po-jobpick">
        <div className="po-jobpick-context">
          <span className={`raci-badge raci-${String(role || "").toLowerCase()}`}>
            {role}
          </span>
          <span className="po-jobpick-context-role">{roleLabel}</span>
          <span className="po-jobpick-context-sep">on</span>
          <span className="po-jobpick-context-task">{taskName || "this task"}</span>
        </div>

        {/* The change, stated before it is made: what is being replaced, by what,
            and what that does to the rate. The TO side stays an empty slot until
            a job is picked, so the dialog always shows its own progress. */}
        <div className="po-jobpick-change">
          <div className="po-jobpick-side">
            <div className="po-jobpick-side-label">Currently assigned</div>
            <div className="po-jobpick-side-name">{currentName}</div>
            <div className="po-jobpick-side-rate">{fmtRate(currentRate)}</div>
          </div>

          <div className="po-jobpick-arrow" aria-hidden="true">→</div>

          <div className={`po-jobpick-side is-target${chosen ? " is-filled" : ""}`}>
            <div className="po-jobpick-side-label">Reassign to</div>
            {chosen ? (
              <>
                <div className="po-jobpick-side-name">{chosen.job_name}</div>
                <div className="po-jobpick-side-rate">
                  {fmtRate(chosen.hourly_rate)}
                  <RateDelta delta={delta} money={money} />
                </div>
              </>
            ) : (
              <div className="po-jobpick-side-empty">Pick a job below</div>
            )}
          </div>
        </div>

        {alternatives.length === 0 ? (
          <div className="po-jobpick-empty">
            No other job is available to assign here. The job list could not be
            loaded — reopen this dialog to retry, or reassign this role in the CMS
            task editor instead.
          </div>
        ) : (
          <>
            <div className="po-jobpick-toolbar">
              <div className="po-jobpick-searchwrap">
                <span className="po-jobpick-searchicon">
                  <SearchIcon />
                </span>
                <input
                  type="text"
                  className="po-jobpick-search"
                  placeholder="Filter jobs by name…"
                  value={query}
                  disabled={busy}
                  onChange={(e) => setQuery(e.target.value)}
                  aria-label="Filter jobs by name"
                />
              </div>
              <span className="po-jobpick-count" aria-live="polite">
                {query.trim()
                  ? `${filtered.length} of ${alternatives.length}`
                  : `${alternatives.length} job${alternatives.length === 1 ? "" : "s"}`}
              </span>
            </div>

            <div
              className="po-jobpick-list"
              role="radiogroup"
              aria-label={`Jobs available for ${roleLabel}`}
            >
              {groups.map((group) => (
                <div className="po-jobpick-group" key={group.key}>
                  <div className="po-jobpick-grouphead">
                    {group.label}
                    <span className="po-jobpick-groupcount">{group.rows.length}</span>
                  </div>

                  {group.rows.map((c) => {
                    const isSel = String(c.job_id) === String(selectedId);
                    const d =
                      currentRate != null && c.hourly_rate != null
                        ? c.hourly_rate - currentRate
                        : null;
                    return (
                      <label
                        key={c.job_id}
                        className={`po-jobpick-option${isSel ? " is-selected" : ""}`}
                      >
                        <input
                          type="radio"
                          name="po-jobpick"
                          className="po-jobpick-radio"
                          checked={isSel}
                          onChange={() => setSelectedId(String(c.job_id))}
                          disabled={busy}
                        />
                        <span className="po-jobpick-dot" aria-hidden="true" />

                        <span className="po-jobpick-option-body">
                          <span className="po-jobpick-option-name">{c.job_name}</span>
                          <span className="po-jobpick-option-tags">
                            {c.skill_match_percentage != null && (
                              <span
                                className="po-jobpick-chip"
                                title="Skill match against the task's required skills, as reported by the optimization"
                              >
                                {Number(c.skill_match_percentage).toFixed(0)}% skill match
                              </span>
                            )}
                            {/* Advisory only — every job stays selectable. */}
                            {c.eligibility === "mismatch" && (
                              <span
                                className="po-jobpick-chip is-mismatch"
                                title="The optimization did not list this job as skill-qualified for this role. Advisory only — you can still assign it."
                              >
                                ⚠ skill mismatch
                              </span>
                            )}
                            {c.eligibility === "unreported" && (
                              <span
                                className="po-jobpick-chip is-unreported"
                                title="No skill-eligibility data came back for this role, so the skill fit is unknown rather than wrong."
                              >
                                skill fit not reported
                              </span>
                            )}
                            {c.company_name && (
                              <span className="po-jobpick-chip is-quiet">
                                {c.company_name}
                              </span>
                            )}
                          </span>
                        </span>

                        <span className="po-jobpick-option-figures">
                          <span className="po-jobpick-option-rate">
                            {fmtRate(c.hourly_rate)}
                          </span>
                          <RateDelta delta={d} money={money} compact />
                        </span>
                      </label>
                    );
                  })}
                </div>
              ))}

              {filtered.length === 0 && (
                <div className="po-jobpick-empty">
                  No job matches “{query.trim()}”.
                </div>
              )}
            </div>
          </>
        )}

        {chosen?.eligibility === "mismatch" && (
          <div className="po-jobpick-warn">
            <b>{chosen.job_name}</b> was not listed as skill-qualified for this
            role. This is advisory — the reassignment is allowed and will be scored
            normally; the process-level skill banner will report the gap.
          </div>
        )}

        {delta != null && delta > 0 && (
          <div className="po-jobpick-warn">
            This job costs more per hour than the current holder, so cost per case
            will rise. The backend re-score will show by how much.
          </div>
        )}

        {error && (
          <div className="po-jobpick-error" role="alert">
            {error}
          </div>
        )}

        <div className="po-jobpick-note">
          Applying re-scores time, cost, quality and flexibility through the
          backend what-if engine and updates every tab. Nothing is written to the
          process until you press <b>Save</b>, which records the change as a new
          version — the current version keeps its people.
        </div>
      </div>
    </FormModal>
  );
};

export default RaciJobEditModal;
